import React, { useContext, useEffect, useState } from "react";
import { pageDocPath } from "../../../../helpers";
import { cn } from "../../../lib/helpers";
import { ShowEmoji } from "../../emoji";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas as iconSet } from "@fortawesome/free-solid-svg-icons";
import { SiteContext } from "../../global/site-context";
import PortableText from "../../portableText";
import { includePromos } from "../../sticky-popup";
import { ArrowLeft } from "../../icon/arrow-left";

import * as styles from "./banner.module.css";


export const CLICK_PREFIX = "action.banner--";

const getUrl = (b) =>
  b.bannerLink === "elsewhere"
    ? b.externalUrl
    : b._type === "event" || b.bannerLink === "resources"
      ? pageDocPath(b)
      : b.bannerLink
        ? `${b.bannerLink}/${b.slug.current}`
        : null;

const DEFAULT_BANNER = {
  _id: "default",
  _type: "banner",
  _rawBanner: [
    {
      _key: "default",
      _type: "block",
      children: [
        {
          _key: "default",
          _type: "span",
          marks: [],
          text: "Thanks for your curiousity.",
        },
      ],
      markDefs: [],
      style: "normal",
    },
  ],
  emoji: "lock",
  externalUrl: "https://www.commonroom.io/",
};

export const Banner = () => {
  const site = useContext(SiteContext);
  const [pastBannerCount, setBannerCount] = useState(0);
  const [banner, setBanner] = useState();

  useEffect(() => {
    if (!site.banners.length > 0) return;

    const unseenBanners = site.banners.filter((b) =>
      // avoid if already clicked
      site.metrics.analytics?.storage?.getItem(`${CLICK_PREFIX}${b._id}`)
        ? false
        : true
    );
    setBanner(unseenBanners?.[0] || null);
  }, [site.banners]);

  useEffect(() => {
    let i = 0;
    let found = 0;
    while (i < localStorage.length) {
      const val = localStorage.key(i);
      if (val.startsWith(CLICK_PREFIX)) {
        found++;
      }
      i++;
    }
    if (found) {
      setBannerCount(found);
    }
  }, []);

  return (banner && includePromos(site)) ? (
    <a
      href={getUrl(banner)}
      className="group"
      {...(banner.bannerLink === "elsewhere" && { target: "_blank", rel: "noopener noreferrer" })}
      onClick={() => {
        site.metrics.logEvent({
          category: "button_click",
          action: "banner",
          label: getUrl(banner),
        });
        localStorage.setItem(`${CLICK_PREFIX}${banner._id}`, true);
      }}
    >
      <BannerContent
        isDarkBg={site.isDarkBg}
        banner={banner}
        pastBannerCount={pastBannerCount}
      />
    </a>
  ) : null;
};

const BannerContent = ({ banner }) => (
  <div
    className={cn(
      "relative z-100 flex justify-center items-center py-4 text-sm sm:text-base leading-snug font-medium",
      "bg-black text-white",
      styles.bannerLight
    )}
    id="promo-banner"
  >
    {banner?.icon && iconSet[banner.icon] ? (
      <FontAwesomeIcon
        icon={iconSet[banner.icon]}
        className="ml-4 mr-4 text-white text-2xl sm:text-xl flex-shrink-0 transform transition-size scale-100 group-hover:scale-125"
      />
    ) : (
      <ShowEmoji
        name={banner?.emoji}
        className="ml-4 mr-4 w-9 sm:w-7 transform transition-size scale-100 group-hover:scale-125"
      />
    )}
    <div className="group-hover:underline">
      <PortableText blocks={banner?._rawBanner} />
    </div>
    <span className="ml-2 mr-8 relative top-px flex items-center">
      <ArrowLeft size={16} rotate={180} />
    </span>
  </div>
);

/**
 * Return banner if appropriate for page and user.
 * Doc type include assets and events.
 *
 * @todo use path for banner.externalUrl
 */
export const getBanners = ({ doc, assets = [] }) => {
  const path = doc ? pageDocPath(doc).replace(/^\/|\/$/g, "") : null;

  // skip landing pages and docs
  if ([
    "documentation", "integration", "redirect", "category", "competitor", "prospect", "company", "invite", "competeDB",
  ].includes(
    doc._type
  ) ||
    path === null ||
    !includePromos(doc) ||
    path.match(/\/hello\//) ||
    doc.mode === "hello"
  )
    return false;

  // Collect matching IDs from both path and category checks, then filter the
  // original assets array to preserve its publishedAt DESC sort order.
  const pathMatches = new Set(
    assets
      .filter((b) => {
        if (b.paths.includes(path) || (path === "" && b.paths.includes("/"))) {
          return true;
        }
        return b.paths.find((p) =>
          p.match(/.*\*$/) && p !== `${path}/*`
            ? path.match(new RegExp(p))
            : p === path || (path === "" && b.paths.includes("/"))
        );
      })
      .map((b) => b._id)
  );

  const categoryMatches = new Set(
    doc.categories
      ? assets
        .filter((a) =>
          doc.categories.some((c) =>
            a.categories.find((ac) => c._id === ac._id)
          )
        )
        .map((a) => a._id)
      : []
  );

  return assets
    .filter(
      (b) =>
        (pathMatches.has(b._id) || categoryMatches.has(b._id)) &&
        (b._id !== doc._id || b.externalUrl !== pageDocPath(doc))
    )
    .sort((a, b) => b.isFeatured - a.isFeatured);
};

